import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { useAxios } from '../../core/_axios';
import useAccount from '../../hooks/useAccount';
import usePageTitle from '../../hooks/usePageTitle';
import { usePageHeader } from '../../contexts/PageHeaderContext';
import QRCode from 'react-qr-code';

interface QrCode {
  unique_identifier: string;
  slug: string;
  link_config: {
    type?: string;
    service_unique_identifier?: string;
  };
  metadata: {
    name?: string;
  };
  is_active: boolean;
  scan_count: number;
  inserted_at: string;
  url: string;
}

interface Service {
  unique_identifier: string;
  public_data: {
    deceased_person: {
      full_name: string;
      avatar_url?: string;
    };
  };
}

export function QrCodesList() {
  const { currentUserAccountUniqueId } = useAuth();
  const axios = useAxios();
  const navigate = useNavigate();
  const { account } = useAccount({ axios, isForCurrentAccount: true });
  const { setPageHeader } = usePageHeader();
  const [qrCodes, setQrCodes] = useState<QrCode[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [baseSlug, setBaseSlug] = useState('');
  const [enabling, setEnabling] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  usePageTitle('QR Codes');

  useEffect(() => {
    setPageHeader('QR Codes');
    return () => setPageHeader(null);
  }, [setPageHeader]);

  useEffect(() => {
    if (!currentUserAccountUniqueId) {
      setLoading(false);
      return;
    }

    const fetchStatus = async () => {
      try {
        setLoading(true);
        const statusResponse = await axios.get('/api/v1/app/authenticated/current-account/qr-codes/status');

        if (statusResponse.data.success) {
          setEnabled(statusResponse.data.enabled);

          // Set base slug if available
          if (statusResponse.data.funeral_link_base_slug) {
            setBaseSlug(statusResponse.data.funeral_link_base_slug);
          }

          if (statusResponse.data.enabled) {
            // If enabled, fetch QR codes and services
            const [qrCodesResponse, servicesResponse] = await Promise.all([
              axios.get('/api/v1/app/authenticated/current-account/qr-codes'),
              axios.get('/api/v1/app/authenticated/services')
            ]);

            if (qrCodesResponse.data.success) {
              // Filter out archived (inactive) QR codes
              const activeQrCodes = qrCodesResponse.data.qr_codes.filter((qr: any) => qr.is_active !== false);
              setQrCodes(activeQrCodes);
            } else {
              setError(qrCodesResponse.data.error || 'Failed to load QR codes');
            }

            const servicesData = servicesResponse.data.data?.services || servicesResponse.data.services || [];
            setServices(servicesData);
          }
        } else {
          setError(statusResponse.data.error || 'Failed to check status');
        }
      } catch (err: any) {
        console.error('Error fetching QR codes:', err);
        setError(err.response?.data?.error || 'Failed to load QR codes');
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, [currentUserAccountUniqueId, axios]);

  // Set base slug when account is loaded and QR codes are not enabled
  useEffect(() => {
    if (account && !enabled && !baseSlug) {
      if (account.name === 'My First Account') {
        const adjectives = ['happy', 'peaceful', 'gentle', 'caring', 'loving', 'serene', 'graceful', 'cherished', 'beloved', 'honored'];
        const nouns = ['oak', 'pine', 'cedar', 'willow', 'rose', 'lily', 'haven', 'gardens', 'chapel', 'memorial'];
        const randomNum = Math.floor(Math.random() * 99) + 1;
        const randomSlug = `${adjectives[Math.floor(Math.random() * adjectives.length)]}-${nouns[Math.floor(Math.random() * nouns.length)]}-${randomNum}`;
        setBaseSlug(randomSlug);
      } else {
        setBaseSlug(account.name.toLowerCase().replace(/[^a-z0-9-]/g, '-'));
      }
    }
  }, [account, enabled, baseSlug]);

  const handleEnable = async () => {
    try {
      setEnabling(true);
      setError(null);

      const response = await axios.post('/api/v1/app/authenticated/current-account/qr-codes/enable', {
        base_slug: baseSlug.trim()
      });

      if (response.data.success) {
        // Navigate to create page to make first QR code
        navigate('/qr-codes/create');
      } else {
        setError(response.data.error || 'Failed to enable QR codes');
      }
    } catch (err: any) {
      console.error('Error enabling QR codes:', err);
      setError(err.response?.data?.error || 'Failed to enable QR codes');
    } finally {
      setEnabling(false);
    }
  };

  if (loading) {
    return (
      <div className="container-fixed">
        <div className="flex justify-center items-center py-12">
          <div className="spinner spinner-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fixed">
        <div className="alert alert-error">
          <i className="ki-filled ki-information-2 text-lg"></i>
          <div className="flex flex-col gap-1">
            <div className="font-semibold">Error</div>
            <div className="text-sm">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!enabled) {
    // Use ngrok in dev, production domain in prod
    const linkDomain = import.meta.env.DEV
      ? 'https://signature-guestbook-link.ngrok.io'
      : 'https://sgb.funeral.link';
    const previewUrl = `${linkDomain}/fl/${baseSlug || 'your-link'}/example`;

    return (
      <div className="container-fixed max-w-2xl">
        <div className="card p-8 text-center flex flex-col items-center">
          <i className="ki-filled ki-barcode text-6xl text-gray-300 mb-4"></i>
          <h3 className="text-xl font-semibold mb-2">Enable QR Codes</h3>
          <p className="text-gray-600 mb-2">
            Claim your link to start creating QR codes.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Each QR code you create can link to different services, events, forms, or any page you need.
          </p>

          <div className="w-full max-w-md space-y-4">
            <div>
              <label className="form-label">Your Link</label>
              <div className="flex items-center gap-0 mb-2">
                <span className="text-sm text-gray-500 bg-gray-100 px-3 py-2 rounded-l border border-r-0 border-gray-300">
                  {linkDomain.replace('https://', '')}/
                </span>
                <input
                  type="text"
                  className="input rounded-l-none flex-1"
                  placeholder="your-funeral-home"
                  value={baseSlug}
                  onChange={(e) => setBaseSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                  autoFocus
                />
              </div>
              <p className="text-sm text-gray-500">
                All your QR codes will use this base link.
              </p>
            </div>

            {/* Preview Checkbox */}
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={showPreview}
                onChange={(e) => setShowPreview(e.target.checked)}
                className="checkbox checkbox-sm"
              />
              <span className="text-gray-600">Preview QR code</span>
            </label>

            {/* QR Code Preview */}
            {showPreview && (
              <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                {baseSlug ? (
                  <>
                    <QRCode
                      value={previewUrl}
                      size={200}
                      style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                      viewBox={`0 0 200 200`}
                    />
                    <p className="text-xs text-gray-500 mt-2 break-all">{previewUrl}</p>
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <p className="text-sm">Enter your link above to see preview</p>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-center">
              <button
                onClick={handleEnable}
                className="btn btn-primary"
                disabled={enabling || !baseSlug.trim()}
              >
                {enabling ? (
                  <>
                    <span className="spinner spinner-white spinner-sm"></span>
                    Setting up...
                  </>
                ) : (
                  <>
                    Continue
                    <i className="ki-filled ki-arrow-right"></i>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Filter QR codes by search query
  const filteredQrCodes = qrCodes.filter(qrCode => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const name = (qrCode.metadata?.name || qrCode.slug).toLowerCase();
    const slug = qrCode.slug.toLowerCase();
    return name.includes(query) || slug.includes(query);
  });

  // Create a lookup map for services
  const servicesMap = new Map(services.map(s => [s.unique_identifier, s]));

  // Helper to get linked service
  const getLinkedService = (qrCode: QrCode) => {
    if (qrCode.link_config?.type === 'service' && qrCode.link_config?.service_unique_identifier) {
      return servicesMap.get(qrCode.link_config.service_unique_identifier);
    }
    return null;
  };

  return (
    <div className="container-fixed">
      <div className="grid gap-5 lg:gap-7.5">
        <div className="card card-grid min-w-full">
          <div className="card-header flex-wrap py-3">
            <div className="relative flex-1 max-w-md">
              <i className="ki-filled ki-magnifier leading-none text-md text-gray-500 absolute top-1/2 left-0 -translate-y-1/2 ml-3"></i>
              <input
                type="text"
                className="input input-sm pl-8 w-full"
                placeholder={`Search ${qrCodes.length} QR codes...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoComplete="off"
              />
              {searchQuery && (
                <button
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setSearchQuery('')}
                >
                  <i className="ki-filled ki-cross-circle"></i>
                </button>
              )}
            </div>
            <Link to="/qr-codes/create" className="btn btn-sm btn-primary">
              <i className="ki-filled ki-plus-squared text-sm me-1"></i>
              Create QR Code
            </Link>
          </div>
          <div className="card-body">
            <div data-datatable="true" data-datatable-page-size="10" id="qr_codes_table">
              <div className="scrollable-x-auto">
                <table className="table table-fixed table-border" data-datatable-table="true">
                  {filteredQrCodes.length > 0 && <thead>
                    <tr>
                      <th className="w-[350px]">
                        <span className="sort asc">
                          <span className="sort-label">Name</span>
                        </span>
                      </th>
                      <th>
                        <span className="sort">
                          <span className="sort-label">Linked To</span>
                        </span>
                      </th>
                      <th className="w-[100px] text-center">
                        <span className="sort">
                          <span className="sort-label">Scans</span>
                        </span>
                      </th>
                      <th className="w-[120px]">
                        <span className="sort">
                          <span className="sort-label">Actions</span>
                        </span>
                      </th>
                    </tr>
                  </thead>}
                  {filteredQrCodes.length > 0 ? <tbody>
                    {filteredQrCodes.map((qrCode) => {
                      const linkedService = getLinkedService(qrCode);
                      const qrUrl = qrCode.url;

                      return (
                        <tr key={qrCode.unique_identifier}>
                          <td>
                            <div className="flex items-center gap-2">
                              <Link
                                to={`/qr-codes/${qrCode.unique_identifier}`}
                                className="font-semibold text-sm text-gray-900 hover:text-primary"
                              >
                                {qrCode.metadata?.name || qrCode.slug}
                              </Link>
                              {!qrCode.is_active && (
                                <span className="badge badge-sm badge-secondary">Inactive</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="flex items-center justify-between gap-3">
                              {linkedService ? (
                                <Link
                                  to={`/services/${linkedService.unique_identifier}/details`}
                                  className="flex items-center gap-2 hover:text-primary"
                                >
                                  {linkedService.public_data.deceased_person?.avatar_url && (
                                    <img
                                      src={linkedService.public_data.deceased_person.avatar_url}
                                      alt=""
                                      className="w-8 h-8 rounded-full object-cover"
                                    />
                                  )}
                                  {!linkedService.public_data.deceased_person?.avatar_url && (
                                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                                      <i className="ki-filled ki-profile-circle text-gray-400 text-xl"></i>
                                    </div>
                                  )}
                                  <span className="text-sm font-medium">
                                    {linkedService.public_data.deceased_person?.full_name || 'Unnamed Service'}
                                  </span>
                                </Link>
                              ) : (
                                <span className="text-sm text-warning">Not configured</span>
                              )}
                              <a
                                href={qrUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-primary hover:underline whitespace-nowrap"
                              >
                                View Link <i className="ki-filled ki-arrow-right text-xs"></i>
                              </a>
                            </div>
                          </td>
                          <td className="text-center">
                            <span className="text-lg font-semibold">{qrCode.scan_count}</span>
                          </td>
                          <td>
                            <Link
                              to={`/qr-codes/${qrCode.unique_identifier}`}
                              className="btn btn-sm btn-light"
                            >
                              <i className="ki-filled ki-setting-2"></i>
                              Manage
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody> : <tbody>
                    <tr>
                      <td colSpan={4} className="text-center">
                        <div>{searchQuery ? 'No QR codes found' : 'No QR codes yet'}</div>
                        {!searchQuery && (
                          <div className="mt-3">
                            <Link to="/qr-codes/create" className="btn btn-sm btn-light">
                              Create QR Code
                            </Link>
                          </div>
                        )}
                      </td>
                    </tr>
                  </tbody>}
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
